import { Component, DestroyRef, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ChoosePlanBuyng } from '../../models/choose-plan/choose-plan-buyng';
import { ClientData } from '../../models/client-data/client-data';
import { ConstantsFordProtect } from '../../models/constants/constants-ford-protect';
import { ContractSummary } from '../../models/contract-summary/contract-summary';
import { ContractSignature } from '../../models/contract/contract-signature';
import { CreateContractSignature } from '../../models/contract/create-contract-signature';
import { DownloadFile } from '../../models/contract/download-file';
import { Dealer } from '../../models/dealer/dealer';
import { DealerSeller } from '../../models/dealer/dealer-seller';
import { FileType } from '../../models/enum/file-type';
import { FileFull } from '../../models/files/file-full';
import { VinValidate } from '../../models/vin-validate/vin-validate';
import { ContractService } from '../../services/contract/contract.service';
import { DealerService } from '../../services/dealer/dealer.service';
import { ImageService } from '../../services/image/image.service';
import { MessageExceptionService } from '../../services/message-exception/message-exception.service';
import { ContractSignatureDraftService } from '../../services/contract/contract-signature-draft.service';
import { SignaturePadComponent } from '../../shared/components/signature-pad/signature-pad.component';
import { ValidationErrorMapperService } from 'src/app/services/address/validation-error-mapper.service';

@Component({
  selector: 'app-contract-signature',
  templateUrl: './contract-signature.component.html',
  styleUrls: ['./contract-signature.component.scss'],
  standalone: false
})
export class ContractSignatureComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  contractIdDownload = 0;
  isContractCreated = false;
  isContractActive = false;
  invalidSignature = true;
  termsUser = false;
  isSubmitted = false;
  isCtaSuccess = false;
  isDownloadingContract = false;
  blockedUi = false;
  dateAttachment = new Date();
  signatureImgBase64: string = '';
  productCode: string = '';
  contract: number = 0;
  document: string = '';
  country: string = '';
  sellerCode: number = 0;
  pandAC: string = '';
  uuid: string = '';
  errorContract = false;

  clientData!: ClientData;
  contractSummary: ContractSummary;
  choosePlanBuyng: ChoosePlanBuyng;
  vinValidate!: VinValidate;
  contractSignatureForm!: UntypedFormGroup;
  contractSignature: ContractSignature | null;
  createContractSignature!: CreateContractSignature;

  @ViewChild('signaturePad') signaturePadCmp?: SignaturePadComponent;
  @ViewChild('termsConsent', { read: ElementRef }) termsConsentRef?: ElementRef<HTMLElement>;
  submitted = false;
  isDrawing = false;
  termsFocusActive = false;

  constructor(
    private messageExceptionService: MessageExceptionService,
    private contractService: ContractService,
    private activatedRoute: ActivatedRoute,
    private messageService: MessageService,
    private fb: UntypedFormBuilder,
    private dealerService: DealerService,
    private translateService: TranslateService,
    private imageService: ImageService,
    private validationErrorMapperService: ValidationErrorMapperService,
    private contractSignatureDraftService: ContractSignatureDraftService

  ) {

    this.choosePlanBuyng = new ChoosePlanBuyng;
    this.contractSummary = new ContractSummary();
    this.contractSignature = new ContractSignature;
  }

  ngOnInit() {

    this.contractSignatureForm = this.fb.group({

      termsUser: [false, Validators.requiredTrue],
      signature: [null, Validators.required],
    });

    this.contractSignatureForm.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(({ termsUser, signature }) => {
      this.termsUser = !!termsUser;
      this.isCtaSuccess = this.termsUser && !!signature;
      this.contractSignatureDraftService.saveDraft(this.uuid, {
        termsUser: this.termsUser,
        signature: signature || null
      });
    });

    this.activatedRoute.queryParams.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(params => {

      this.uuid = params.uuid;
      this.restoreDraftState();
      this.contractSummarySearch(this.uuid);
    });

  }

  contractSummarySearch(uuid: string) {
    setTimeout(() => {

      this.contractService.getContractSignature(uuid).pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((response: ContractSignature) => {
        this.contractSignature = response;
        this.country = this.contractSignature.country;
        this.sellerCode = this.contractSignature.sellerK;
        this.pandAC = this.contractSignature.dealerPAndAc;

        if (this.country === ConstantsFordProtect.BRA) { // taking contract only BRA type PREB

          this.getSellerAutomatic(this.sellerCode);
          this.getDealerAutomatic(this.country, this.pandAC);


          this.productCode = this.contractSignature.codeProduct;

          if (this.productCode !== ConstantsFordProtect.PREB) {
            this.productCode = this.contractSignature.extension.codeProduct;
          }

        } else {
          this.productCode = this.contractSignature.codeProduct;
        }

        if (this.contractSignature.statusContract === ConstantsFordProtect.CONTRACT_ATIVO_CODE) {
          this.isContractActive = true
          this.contractIdDownload = this.contractSignature.contractId;
          this.contractSignatureDraftService.clearDraft(this.uuid);
        }

      }, (error) => {

        this.errorContract = true;
        this.contractSignature = null;
        switch (error.status) {

          case 401:

            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('TRANSLATE.ERROR'),
              detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
            });
            break;

          case 400:
          case 404:
          case 500:

            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('TRANSLATE.ERROR'),
              detail: this.translateService.instant('TRANSLATE.CONTRACT.NOT.FOUND')
            });
            break;

          default:

            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('TRANSLATE.ERROR'),
              detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
            });
            break;
        }

      });

    }, 1000);
  }

  getDealerAutomatic(country: string, pandAC: string) {

    this.dealerService.getDealerSignature(country, pandAC)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: Dealer) => {
        this.contractSummary.dealer = response;
      });
  }

  getSellerAutomatic(sellerCode: number) {
    this.dealerService.getDealerSellerSignature(sellerCode)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: DealerSeller) => {
        this.contractSummary.dealerSeller = response;
        this.contractSummary.dealerSeller.id = response.id;
        this.contractSummary.dealerSeller.sellerCode = response.sellerCode;
        this.contractSummary.dealerSeller.name = response.name;
        this.contractSummary.dealerSeller.phone = response.phone;
        this.contractSummary.dealerSeller.email = response.email;
        this.contractSummary.dealerSeller.document = response.document;

        if (response.birthday !== null) {
          this.contractSummary.dealerSeller.birthday = new Date(response.birthday);
        }

      });

  }

  get f(): { [key: string]: AbstractControl } {
    return this.contractSignatureForm.controls;
  }

  drawComplete(): void {
    const signatureCtrl = this.contractSignatureForm.get('signature');
    signatureCtrl?.markAsTouched();
    signatureCtrl?.updateValueAndValidity({ onlySelf: true });
    this.syncCtaState();
  }

  onTermsChange(): void {
    this.syncCtaState();
  }

  drawClean(): void {
    this.signaturePadCmp?.clear();
    const signatureCtrl = this.contractSignatureForm.get('signature');
    signatureCtrl?.setValue(null);
    signatureCtrl?.markAsTouched();
    signatureCtrl?.updateValueAndValidity({ onlySelf: true });
  }

  private focusTermsConsent(): boolean {
    const termsHost = this.termsConsentRef?.nativeElement;
    if (!termsHost) {
      return false;
    }

    const termsInput = termsHost.querySelector('.p-checkbox-input') as HTMLInputElement | null;
    const termsBox = termsHost.querySelector('.p-checkbox-box') as HTMLElement | null;
    if (!termsInput && !termsBox) {
      return false;
    }

    this.termsFocusActive = true;
    setTimeout(() => {
      this.termsFocusActive = false;
    }, 1300);

    termsHost.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (termsInput) {
      termsInput.focus();
    } else if (termsBox) {
      termsBox.setAttribute('tabindex', '-1');
      termsBox.focus();
    }
    return true;
  }

  getDay(): number {
    return this.dateAttachment.getDate();
  }

  getMonth(): string {
    const numberMonth = this.dateAttachment.getMonth();
    let nameMonth = '';

    switch (numberMonth) {
      case 0:
        nameMonth = this.translateService.instant('TRANSLATE.MONTH.JANUARY.UPPER.CASE');
        break;
      case 1:
        nameMonth = this.translateService.instant('TRANSLATE.MONTH.FEBRUARY.UPPER.CASE');
        break;
      case 2:
        nameMonth = this.translateService.instant('TRANSLATE.MONTH.MARCH.UPPER.CASE');
        break;
      case 3:
        nameMonth = this.translateService.instant('TRANSLATE.MONTH.APRIL.UPPER.CASE');
        break;
      case 4:
        nameMonth = this.translateService.instant('TRANSLATE.MONTH.MAY.UPPER.CASE');
        break;
      case 5:
        nameMonth = this.translateService.instant('TRANSLATE.MONTH.JUNE.UPPER.CASE');
        break;
      case 6:
        nameMonth = this.translateService.instant('TRANSLATE.MONTH.JULY.UPPER.CASE');
        break;
      case 7:
        nameMonth = this.translateService.instant('TRANSLATE.MONTH.AUGUST.UPPER.CASE');
        break;
      case 8:
        nameMonth = this.translateService.instant('TRANSLATE.MONTH.SEPTEMBER.UPPER.CASE');
        break;
      case 9:
        nameMonth = this.translateService.instant('TRANSLATE.MONTH.OCTOBER.UPPER.CASE');
        break;
      case 10:
        nameMonth = this.translateService.instant('TRANSLATE.MONTH.NOVEMBER.UPPER.CASE');
        break;
      case 11:
        nameMonth = this.translateService.instant('TRANSLATE.MONTH.DECEMBER.UPPER.CASE');
        break;
    }

    return nameMonth;
  }

  getYear(): number {
    return this.dateAttachment.getFullYear();
  }

  private restoreDraftState(): void {
    const draft = this.contractSignatureDraftService.restoreDraft(this.uuid);

    if (!draft) {
      this.termsUser = false;
      this.contractSignatureForm.reset({ termsUser: false, signature: null }, { emitEvent: false });
      this.syncCtaState();
      return;
    }

    this.termsUser = draft.termsUser;
    this.contractSignatureForm.patchValue({
      termsUser: draft.termsUser,
      signature: draft.signature
    }, { emitEvent: false });
    this.syncCtaState();
  }

  private syncCtaState(): void {
    const hasSignature = !!this.contractSignatureForm.get('signature')?.value;
    this.isCtaSuccess = this.termsUser && hasSignature;
  }

  buttonSave() {

    this.submitted = true;
    this.contractSignatureForm.markAllAsTouched();
    this.contractSignatureForm.updateValueAndValidity();

    if (this.contractSignatureForm.invalid) {
      if (this.f['termsUser']?.errors) {
        const focusedTerms = this.focusTermsConsent();
        if (!focusedTerms && this.f['signature']?.errors) {
          setTimeout(() => this.signaturePadCmp?.focusCanvas());
        }
        return;
      }

      if (this.f['signature']?.errors) {
        setTimeout(() => this.signaturePadCmp?.focusCanvas());
      }
      return;
    }

    this.setSignatureImgBase64();
    if (!this.signatureImgBase64) {
      this.contractSignatureForm.get('signature')?.setErrors({ required: true });
      setTimeout(() => this.signaturePadCmp?.focusCanvas());
      return;
    }

    this.save();
  }

  downloadPDF2(): void {
    const element = document.getElementById('pdfSignatureMethod');
    if (!element) return;

    import('jspdf').then(({ jsPDF }) => {
      const doc = new jsPDF({ unit: 'in', format: 'a4', orientation: 'portrait' });
      doc.html(element, {
        callback: (pdf) => {
          pdf.save('TERMO_CONTRATACAO_GARANTIA_ESTENDIDA_REDUZIDA.pdf');
        },
        margin: [0, 0, 0, 0],
        x: 0,
        y: 0,
        width: 8.27,
        windowWidth: element.scrollWidth
      });
    });
  }
  save(): void {

    this.isSubmitted = true;
    this.blockedUi = true;

    this.contractService
      .save(this.setContractSignature())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: CreateContractSignature) => {

        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('TRANSLATE.SUCCESS'),
          detail: this.translateService.instant('TRANSLATE.CONTRACT.CREATED.SUCCESSFULL')
        });

        this.vinValidate = new VinValidate();
        this.choosePlanBuyng = new ChoosePlanBuyng();
        this.clientData = new ClientData();
        this.contractSummary = new ContractSummary();
        this.createContractSignature = new CreateContractSignature();
        this.contractSignature = new ContractSignature;

        this.contractIdDownload = response.contractId;
        this.isContractCreated = true;
        this.blockedUi = false;
        this.contractSignatureDraftService.clearDraft(this.uuid);
      }, (err) => {

        if (err.status === 400 || err.status === 404 || err.status === 500) {
          const mappedError = this.validationErrorMapperService.mapBackendErrorToI18nKey(err);
          const errorKey = mappedError.key.startsWith('TRANSLATE.ERROR.CEP.')
            ? 'TRANSLATE.CONTRACT.COULD.NOT.SAVED.CLOSE.PAGE.LOG.AGAIN'
            : mappedError.key;
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('TRANSLATE.ERROR'),
            detail: this.translateService.instant(errorKey)
          });
        } else {
          this.messageExceptionService.getMessageException(
            err.status,
            this.translateService.instant('TRANSLATE.CONTRACT.COULD.NOT.SAVED.CLOSE.PAGE.LOG.AGAIN')
          );
        }

        this.isSubmitted = false;
        this.blockedUi = false;
        this.isContractCreated = false;
      });
  }

  setSignatureImgBase64(): void {
    this.signatureImgBase64 = this.imageService.getSignatureDataUrl(this.signaturePadCmp!);
  }

  private setContractSignature(): CreateContractSignature {

    const createContractSignature = new CreateContractSignature();

    if (this.contractSignature) {
      createContractSignature.contractId = this.contractSignature.contractId;
    }
    createContractSignature.user = ConstantsFordProtect.EMISSION_USER;
    createContractSignature.country = this.country;

    createContractSignature.files = this.setFileSignature(createContractSignature.files);

    return createContractSignature;
  }

  setFileSignature(files: FileFull): FileFull {

    if (this.country === ConstantsFordProtect.BRA && this.productCode === ConstantsFordProtect.PREB) {

      files.type = FileType.PURCHASE_TERM;
      files.name = FileType.PURCHASE_TERM_NAME;
      files.description = FileType.PURCHASE_TERM_DESCRIPTION;
      files.fileReference = FileType.PURCHASE_TERM.toString();
      files.file = this.signatureImgBase64;
    } else if (this.country === ConstantsFordProtect.CHL) {

      files.type = FileType.CONTRACT_SUCCESS_CH;
      files.name = FileType.CONTRACT_SUCCESS_CH_NAME;
      files.description = FileType.CONTRACT_SUCCESS_CO_AND_CH_DESCRIPTION;
      files.fileReference = FileType.CONTRACT_SUCCESS_CH.toString();
      files.file = this.signatureImgBase64;
    } else if (this.country === ConstantsFordProtect.COL) {

      files.type = FileType.CONTRACT_SUCCESS_CO;
      files.name = FileType.CONTRACT_SUCCESS_CO_NAME;
      files.description = FileType.CONTRACT_SUCCESS_CO_AND_CH_DESCRIPTION;
      files.fileReference = FileType.CONTRACT_SUCCESS_CO.toString();
      files.file = this.signatureImgBase64;
    }

    return files;
  }

  downloadPDF() {
    if (this.isDownloadingContract) {
      return;
    }

    this.isDownloadingContract = true;
    this.contractService.downloadFile(this.contractIdDownload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: DownloadFile) => {
        const source = response.pdfByteArray;
        const link = document.createElement("a");
        link.href = source;
        link.download = FileType.CONTRACT_TERMS_NAME;
        link.click();

        this.isDownloadingContract = false;
      }, (error) => {

        this.isDownloadingContract = false;
        switch (error.status) {

          case 401:

            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('TRANSLATE.ERROR'),
              detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
            });
            break;

          case 400:
          case 404:
          case 500:

            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('TRANSLATE.ERROR'),
              detail: this.translateService.instant('TRANSLATE.FILE.NOT.FOUND')
            });
            break;

          default:

            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('TRANSLATE.ERROR'),
              detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
            });
            break;
        }

      });
  }

}
